import React, { useState, useCallback } from 'react';

/* --------------------------------------------------------------------------
   Skool targets: which Skool account posts into which group and category.

   Shared by the Skool composer (routes/SkoolPost.js) and the single-post editor
   (routes/single-post/SinglePost.js). Groups and categories come from Skool
   through api.schedpilot.com (lib/skool.php) and are loaded on demand.

   targets: { [accountId]: { group_id, group_name, group_slug, label_id, label_name } }
   An account is selected when it has an entry.
   -------------------------------------------------------------------------- */

export const SKOOL_TITLE_MAX = 150;   // keep in step with SP_SKOOL_TITLE_MAX in lib/skool.php
export const SKOOL_BODY_MAX = 10000;  // SP_SKOOL_BODY_MAX
export const SKOOL_MAX_IMAGES = 10;   // SP_SKOOL_MAX_IMAGES

const API = 'https://api.schedpilot.com/wp-json/api/v1';
const emptyTarget = { group_id: '', group_name: '', group_slug: '', label_id: '', label_name: '' };

async function apiGet(path) {
  try {
    const res = await fetch(API + path, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && (data.code ?? 200) < 400, status: res.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: { message: 'Could not reach SchedPilot.' } };
  }
}

/** State and actions for picking Skool accounts, groups and categories. */
export function useSkoolTargets(onNeedsReconnect) {
  const [targets, setTargets] = useState({});
  const [groups, setGroups] = useState({});   // accountId -> { status, items, message }
  const [labels, setLabels] = useState({});   // `${accountId}:${groupId}` -> { status, items, message }

  const loadGroups = useCallback(async (accountId) => {
    setGroups((g) => ({ ...g, [accountId]: { status: 'loading', items: [] } }));
    const r = await apiGet(`/skool/${accountId}/groups`);
    if (r.ok) {
      setGroups((g) => ({ ...g, [accountId]: { status: 'ok', items: r.data.items || [] } }));
    } else {
      if (r.data.error === 'SKOOL_NEEDS_RECONNECT' && onNeedsReconnect) onNeedsReconnect();
      setGroups((g) => ({ ...g, [accountId]: { status: 'error', items: [], message: r.data.message || 'Could not load your Skool groups.' } }));
    }
  }, [onNeedsReconnect]);

  const loadLabels = useCallback(async (accountId, groupId, groupSlug) => {
    const key = `${accountId}:${groupId}`;
    setLabels((l) => ({ ...l, [key]: { status: 'loading', items: [] } }));
    const slug = groupSlug ? `?slug=${encodeURIComponent(groupSlug)}` : '';
    const r = await apiGet(`/skool/${accountId}/groups/${encodeURIComponent(groupId)}/labels${slug}`);
    setLabels((l) => ({
      ...l,
      [key]: r.ok ? { status: 'ok', items: r.data.items || [] }
                  : { status: 'error', items: [], message: r.data.message || 'Could not load the categories.' },
    }));
  }, []);

  /** Replace all targets at once (editing an existing post) and load what they need. */
  const loadTargets = useCallback((list) => {
    const next = {};
    (list || []).forEach((t) => { next[t.account_id] = { ...emptyTarget, ...t }; });
    setTargets(next);
    Object.entries(next).forEach(([accountId, t]) => {
      loadGroups(accountId);
      if (t.group_id) loadLabels(accountId, t.group_id, t.group_slug);
    });
  }, [loadGroups, loadLabels]);

  const toggleAccount = (account) => {
    const selected = !!targets[account.id];
    setTargets((t) => {
      const next = { ...t };
      if (next[account.id]) delete next[account.id];
      else next[account.id] = { ...emptyTarget };
      return next;
    });
    if (!selected && !groups[account.id]) loadGroups(account.id);
  };

  const chooseGroup = (accountId, groupId) => {
    const group = (groups[accountId]?.items || []).find((g) => String(g.id) === groupId);
    setTargets((t) => ({
      ...t,
      [accountId]: { group_id: group ? String(group.id) : '', group_name: group?.name || '', group_slug: group?.slug || '', label_id: '', label_name: '' },
    }));
    if (group && !labels[`${accountId}:${group.id}`]) loadLabels(accountId, String(group.id), group.slug);
  };

  const chooseLabel = (accountId, labelId) => {
    const t = targets[accountId];
    const item = (labels[`${accountId}:${t.group_id}`]?.items || []).find((l) => String(l.id) === labelId);
    setTargets((all) => ({ ...all, [accountId]: { ...all[accountId], label_id: item ? String(item.id) : '', label_name: item?.name || '' } }));
  };

  /** What still stands between the user and saving, in plain words. */
  const problems = (accounts) => {
    const out = [];
    const chosen = accounts.filter((a) => targets[a.id]);
    if (!chosen.length) out.push('Choose a Skool account');
    chosen.forEach((a) => {
      const t = targets[a.id];
      if (a.needs_reconnect) out.push(`Reconnect ${a.name} from the SchedPilot extension`);
      else if (!t.group_id) out.push(`Choose a group for ${a.name}`);
      else {
        const l = labels[`${a.id}:${t.group_id}`];
        if (l?.status === 'ok' && l.items.length && !t.label_id) out.push(`Choose a category for ${a.name}`);
      }
    });
    return out;
  };

  /** The targets as the API expects them. */
  const targetList = (accounts) =>
    accounts.filter((a) => targets[a.id]).map((a) => ({ account_id: Number(a.id), ...targets[a.id] }));

  return { targets, groups, labels, loadGroups, loadLabels, loadTargets, toggleAccount, chooseGroup, chooseLabel, problems, targetList };
}

/** One row per Skool account: a checkbox, then group and category pickers once selected. */
export function SkoolTargetRows({ accounts, skool, onConnect }) {
  const { targets, groups, labels, loadGroups, toggleAccount, chooseGroup, chooseLabel } = skool;

  if (!accounts.length) {
    return (
      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
        No Skool account connected yet.{' '}
        {onConnect && (
          <button type="button" onClick={onConnect} style={{ border: 'none', background: 'none', padding: 0, color: '#0f172a', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Connect Skool</button>
        )}
      </div>
    );
  }

  return accounts.map((a) => {
    const on = !!targets[a.id];
    const g = groups[a.id];
    const t = targets[a.id];
    const l = t?.group_id ? labels[`${a.id}:${t.group_id}`] : null;
    // The saved group may be missing from the list (left the group, or Skool
    // still loading); keep showing it so an edit never silently drops it.
    const groupItems = g?.items || [];
    const showSavedGroup = t?.group_id && !groupItems.some((x) => String(x.id) === t.group_id);
    const labelItems = l?.items || [];
    const showSavedLabel = t?.label_id && !labelItems.some((x) => String(x.id) === t.label_id);
    return (
      <div key={a.id} style={{ border: '1px solid ' + (on ? '#cbd5e1' : '#f1f5f9'), borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: on ? '#f8fafc' : '#fff' }}>
        <label htmlFor={`skool-acc-${a.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
          <input id={`skool-acc-${a.id}`} type="checkbox" checked={on} onChange={() => toggleAccount(a)} style={{ width: 16, height: 16 }} />
          <span style={{ fontWeight: 600, fontSize: '0.88rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
          {a.needs_reconnect && <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 }}>Needs reconnect</span>}
        </label>

        {on && !a.needs_reconnect && (
          <div className="row g-2" style={{ marginTop: 6 }}>
            <div className="col-12 col-md-6">
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 2 }}>Group</div>
              {g?.status === 'error' ? (
                <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>
                  {g.message} <button type="button" onClick={() => loadGroups(a.id)} style={{ border: 'none', background: 'none', color: '#0f172a', textDecoration: 'underline', padding: 0, fontSize: '0.78rem' }}>Retry</button>
                </div>
              ) : (
                <select className="form-select form-select-sm" value={t.group_id} disabled={!g || g.status === 'loading'} onChange={(e) => chooseGroup(a.id, e.target.value)}>
                  <option value="">{!g || g.status === 'loading' ? 'Loading groups…' : 'Choose a group'}</option>
                  {showSavedGroup && <option value={t.group_id}>{t.group_name || 'Saved group'}</option>}
                  {groupItems.map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                </select>
              )}
            </div>
            <div className="col-12 col-md-6">
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 2 }}>Category</div>
              {l?.status === 'error' ? (
                <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>{l.message}</div>
              ) : l?.status === 'ok' && !labelItems.length && !showSavedLabel ? (
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', paddingTop: 4 }}>This group has no categories.</div>
              ) : (
                <select className="form-select form-select-sm" value={t.label_id} disabled={!t.group_id || !l || l.status === 'loading'} onChange={(e) => chooseLabel(a.id, e.target.value)}>
                  <option value="">{!t.group_id ? 'Choose a group first' : !l || l.status === 'loading' ? 'Loading categories…' : 'Choose a category'}</option>
                  {showSavedLabel && <option value={t.label_id}>{t.label_name || 'Saved category'}</option>}
                  {labelItems.map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                </select>
              )}
            </div>
          </div>
        )}
      </div>
    );
  });
}
